import { FC, useCallback, useState, useEffect } from 'react';
import { IHeaderParams } from 'ag-grid-community';
import SVG from 'react-inlinesvg';
import cx from 'classnames';

import ellipsisSvg from 'assets/icons/ellipsis.svg';
import caretDown from 'assets/icons/arrow_light_down.svg';

type Sort = 'asc' | 'desc' | null;

interface ColumnHeaderProps extends IHeaderParams {
  displayName: string;
}

const ColumnHeader: FC<ColumnHeaderProps> = ({ column, displayName, enableMenu, setSort, showColumnMenu }) => {
  const [activeSort, setActiveSort] = useState<Sort>(null);

  useEffect(() => {
    const updateSortState = (): void => {
      setActiveSort(column.getSort() as Sort);
    };

    column.addEventListener('sortChanged', updateSortState);
    return () => {
      column.removeEventListener('sortChanged', updateSortState);
    };
  }, [column]);

  const handleSortColumn = useCallback((): void => {
    const sort = column.getSort();

    if (sort === 'asc') {
      setSort('desc');
      setActiveSort('desc');
    } else if (sort === 'desc') {
      setSort(null);
      setActiveSort(null);
    } else {
      setSort('asc');
      setActiveSort('asc');
    }
  }, [column, setSort]);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      showColumnMenu(event.currentTarget);
    },
    [showColumnMenu],
  );

  const isSortable = column.getColDef().sortable;

  return (
    <div className="flex items-center justify-between w-full">
      <button
        type="button"
        className={cx('flex items-center gap-1', {
          'cursor-pointer': isSortable,
          'cursor-default': !isSortable,
        })}
        onClick={handleSortColumn}
        disabled={!isSortable}
      >
        <span className="sub-header-sm">{displayName}</span>
        {isSortable && (
          <div className="flex flex-col">
            {(!activeSort || activeSort === 'asc') && (
              <SVG
                src={caretDown}
                className={cx('rotate-180 h-3 w-3', {
                  'text-neutral-950': activeSort === 'asc',
                  'text-neutral-400 -mb-1': !activeSort,
                })}
              />
            )}
            {(!activeSort || activeSort === 'desc') && (
              <SVG
                src={caretDown}
                className={cx('h-3 w-3', {
                  'text-neutral-950': activeSort === 'desc',
                  'text-neutral-400': !activeSort,
                })}
              />
            )}
          </div>
        )}
      </button>
      {enableMenu && (
        <button
          type="button"
          className="flex items-center justify-center w-5 h-5 cursor-pointer hover:bg-neutral-200 rounded"
          onClick={handleMenuClick}
          data-test-id={`column-menu-${column.getColId()}`}
        >
          <SVG className="w-4 h-4 text-neutral-600" src={ellipsisSvg} />
        </button>
      )}
    </div>
  );
};

export default ColumnHeader;
